// Motor de scoring LicitaPro — lógica pura sin dependencias externas.
// Replicado front/back para evitar workspace package que complicaba el build Docker.
// Misma implementación exacta que backend/src/common/scoring/scoring.service.ts


export type NivelCualitativo = "alto" | "medio" | "bajo" | "sin_dato";
export type Recommendation = "postular" | "revisar" | "no_postular";
export type FactorStatus = "ok" | "warn" | "fail";

export interface ScoringCompanyProfile {
  datos: {
    rubros: string[];
    keywords: string[];
    regiones: string[];
  };
  historial: {
    organismosFrecuentes: { nombre: string; adjudicadas: number }[];
    categoriasExitosas: { nombre: string; tasa: number }[];
  };
  capacidades: {
    certificaciones: string[];
  };
  estrategia: {
    presupuestoMin: number;
    presupuestoMax: number;
    regionesPreferidas: string[];
    modalidadesPreferidas: string[];
    toleranciaRiesgo: "baja" | "media" | "alta";
    complejidadAceptada: "baja" | "media" | "alta";
  };
}

/** Input normalizado de licitación. */
export interface ScoringLicitacion {
  nombre: string;
  organismo: string;
  categorias: string[];
  region: string;
  montoEstimado: number | null;
  modalidad: string;
  /** Igual que el motor del backend: el nivel del proceso puede ser `sin_dato`. */
  riesgo: NivelCualitativo;
  /** Descripción oficial (opcional): aporta al match de rubro y keywords. */
  descripcion?: string;
}

export interface MatchFactor {
  key: string;
  label: string;
  weight: number;
  contribution: number;
  status: FactorStatus;
  detail: string;
}

export interface OpportunityScore {
  score: number;
  probabilidad: number;
  recommendation: Recommendation;
  riesgo: NivelCualitativo;
  competencia: NivelCualitativo;
  complejidad: NivelCualitativo;
  factors: MatchFactor[];
  proRazones: string[];
  contraRazones: string[];
}

const clamp = (n: number, min = 0, max = 100) => Math.max(min, Math.min(max, n));

function overlapScore(a: string[], b: string[]) {
  if (!a.length || !b.length) return 0;
  const norm = (s: string) => s.toLowerCase().trim();
  const setA = new Set(a.map(norm));
  let hits = 0;
  for (const x of b) if (setA.has(norm(x))) hits++;
  return hits / Math.max(a.length, b.length);
}

function categoriaOverlap(rubros: string[], categorias: string[]) {
  if (!rubros.length || !categorias.length) return 0;
  const norm = (s: string) => s.toLowerCase().trim();
  const rubrosN = rubros.map(norm);
  let best = 0;
  for (const cat of categorias) {
    const segmentos = norm(cat).split("/").map((s) => s.trim());
    for (const seg of segmentos) {
      if (rubrosN.some((r) => seg.includes(r) || r.includes(seg))) {
        best = Math.max(best, 1);
      }
    }
  }
  if (best > 0) return best;
  return overlapScore(rubros, categorias);
}

function keywordHit(keywords: string[], text: string) {
  if (!keywords.length) return 0;
  const lower = text.toLowerCase();
  let hits = 0;
  for (const k of keywords) if (lower.includes(k.toLowerCase())) hits++;
  return hits / keywords.length;
}

function presupuestoFit(min: number, max: number, monto: number) {
  if (monto < min * 0.5) return 0.2;
  if (monto > max * 1.5) return 0.1;
  if (monto >= min && monto <= max) return 1;
  if (monto < min) return Math.max(0, Math.min(1, monto / min));
  return Math.max(0, Math.min(1, max / monto));
}

function organismoAfinidad(profile: ScoringCompanyProfile, organismo: string) {
  const found = profile.historial.organismosFrecuentes.find((o) => o.nombre === organismo);
  if (!found) return 0;
  return Math.max(0, Math.min(1, found.adjudicadas / 8));
}

function categoriaExitoFactor(profile: ScoringCompanyProfile, categorias: string[]) {
  let best = 0;
  for (const cat of categorias) {
    const found = profile.historial.categoriasExitosas.find((c) => c.nombre === cat);
    if (found) best = Math.max(best, found.tasa);
  }
  return best;
}

const FACTOR_WEIGHTS = {
  rubro: 0.18,
  keyword: 0.12,
  organismo: 0.15,
  categoriaExito: 0.12,
  certificaciones: 0.08,
  presupuesto: 0.12,
  region: 0.08,
  modalidad: 0.05,
  riesgoEmpresa: 0.05,
  complejidad: 0.05,
} as const;

type FactorKey = keyof typeof FACTOR_WEIGHTS;

/** Factores que dependen del historial de adjudicaciones del tenant. */
const FACTORES_HISTORIAL: FactorKey[] = ["organismo", "categoriaExito"];

function sinHistorial(profile: ScoringCompanyProfile): boolean {
  return (
    !profile.historial.organismosFrecuentes.length && !profile.historial.categoriasExitosas.length
  );
}

/**
 * Pesos efectivos: si el perfil no declara historial de adjudicaciones, los
 * factores que dependen de él no pueden puntuar nunca, así que su peso se
 * redistribuye proporcionalmente entre los factores que sí aplican. Con
 * historial declarado los pesos quedan exactamente como están definidos.
 */
function pesosEfectivos(profile: ScoringCompanyProfile): Record<FactorKey, number> {
  const base = { ...FACTOR_WEIGHTS } as Record<FactorKey, number>;
  if (!sinHistorial(profile)) return base;
  const liberado = FACTORES_HISTORIAL.reduce((acc, k) => acc + base[k], 0);
  const resto = 1 - liberado;
  const out = {} as Record<FactorKey, number>;
  for (const k of Object.keys(base) as FactorKey[]) {
    out[k] = FACTORES_HISTORIAL.includes(k) ? 0 : base[k] / resto;
  }
  return out;
}



function levelFromScore(score: number): NivelCualitativo {
  if (score >= 70) return "alto";
  if (score >= 40) return "medio";
  return "bajo";
}

function competenciaEstimada(lic: ScoringLicitacion): NivelCualitativo {
  const monto = lic.montoEstimado ?? 0;
  const seed = (monto / 1_000_000) % 10;
  if (lic.categorias.some((c) => c.includes("Tecnología")) && seed > 6) return "alto";
  if (seed > 7) return "alto";
  if (seed > 4) return "medio";
  return "bajo";
}

function complejidadEstimada(lic: ScoringLicitacion): NivelCualitativo {
  const monto = lic.montoEstimado ?? 0;
  if (monto > 500_000_000) return "alto";
  if (monto > 100_000_000) return "medio";
  return "bajo";
}

export function computeMatch(
  profile: ScoringCompanyProfile,
  licitacion: ScoringLicitacion,
): OpportunityScore {
  const text = `${licitacion.nombre} ${licitacion.descripcion ?? ""} ${licitacion.categorias.join(" ")} ${licitacion.organismo}`;
  const W = pesosEfectivos(profile);
  const noHist = sinHistorial(profile);

  // El rubro puede calzar por categoría UNSPSC o por texto de la licitación
  // (nombre/descripción), porque el UNSPSC oficial rara vez usa el mismo término.
  const sRubro = Math.max(
    categoriaOverlap(profile.datos.rubros, licitacion.categorias),
    keywordHit(profile.datos.rubros, text),
  );
  const sKeyword = keywordHit(profile.datos.keywords, text);
  const sOrganismo = organismoAfinidad(profile, licitacion.organismo);
  const sCategoria = categoriaExitoFactor(profile, licitacion.categorias);
  const sCert = profile.capacidades.certificaciones.length >= 2 ? 0.85 : 0.5;
  const sPresupuesto =
    licitacion.montoEstimado == null
      ? 0.5
      : presupuestoFit(profile.estrategia.presupuestoMin, profile.estrategia.presupuestoMax, licitacion.montoEstimado);
  const sRegion = profile.estrategia.regionesPreferidas.includes(licitacion.region)
    ? 1
    : profile.datos.regiones.includes(licitacion.region)
      ? 0.7
      : 0.3;
  const sModalidad = profile.estrategia.modalidadesPreferidas.includes(licitacion.modalidad) ? 1 : 0.5;
  const sRiesgo =
    licitacion.riesgo === "bajo" ? 1
      : licitacion.riesgo === "medio" ? 0.6
        : profile.estrategia.toleranciaRiesgo === "alta" ? 0.5 : 0.25;
  const complejidad = complejidadEstimada(licitacion);
  const sComplejidad =
    complejidad === "bajo" ? 1
      : complejidad === "medio" ? 0.7
        : profile.estrategia.complejidadAceptada === "alta" ? 0.65 : 0.35;

  const partials = [
    { key: "rubro", label: "Compatibilidad de rubro", w: W.rubro, v: sRubro, detail: sRubro > 0.5 ? "Coincide con tu rubro principal" : "Fuera de tus rubros frecuentes" },
    { key: "keyword", label: "Keywords y especialidades", w: W.keyword, v: sKeyword, detail: sKeyword > 0.3 ? "Coinciden palabras clave del perfil" : "Pocas coincidencias semánticas" },
    { key: "organismo", label: "Historial con el organismo", w: W.organismo, v: sOrganismo, detail: noHist ? "Sin historial declarado — no pondera" : sOrganismo > 0.3 ? "Has adjudicado con este organismo" : "Sin historial relevante" },
    { key: "categoriaExito", label: "Categoría exitosa", w: W.categoriaExito, v: sCategoria, detail: noHist ? "Sin historial declarado — no pondera" : sCategoria > 0.3 ? `Tasa histórica ${Math.round(sCategoria * 100)}%` : "Categoría sin track-record" },
    { key: "certificaciones", label: "Certificaciones", w: W.certificaciones, v: sCert, detail: sCert > 0.7 ? "Certificaciones clave presentes" : "Faltan certificaciones competitivas" },
    { key: "presupuesto", label: "Presupuesto compatible", w: W.presupuesto, v: sPresupuesto, detail: licitacion.montoEstimado == null ? "Monto no público — factor neutro" : sPresupuesto > 0.7 ? "Dentro de tu rango operativo" : "Monto fuera de rango estratégico" },
    { key: "region", label: "Cobertura regional", w: W.region, v: sRegion, detail: sRegion === 1 ? "Región preferida" : sRegion > 0.5 ? "Región operada" : "Región fuera de cobertura" },
    { key: "modalidad", label: "Modalidad afín", w: W.modalidad, v: sModalidad, detail: sModalidad === 1 ? "Modalidad preferida" : "Modalidad no priorizada" },
    { key: "riesgoEmpresa", label: "Riesgo aceptable", w: W.riesgoEmpresa, v: sRiesgo, detail: licitacion.riesgo === "bajo" ? "Riesgo bajo" : licitacion.riesgo === "medio" ? "Riesgo medio controlable" : "Riesgo alto sobre tolerancia" },
    { key: "complejidad", label: "Complejidad manejable", w: W.complejidad, v: sComplejidad, detail: complejidad === "alto" ? "Complejidad alta" : complejidad === "medio" ? "Complejidad media" : "Complejidad baja" },
  ];

  const factors: MatchFactor[] = partials.map((p) => ({
    key: p.key,
    label: p.label,
    weight: p.w,
    contribution: Math.round(p.v * p.w * 100),
    status: p.v >= 0.7 ? "ok" : p.v >= 0.4 ? "warn" : "fail",
    detail: p.detail,
  }));

  const score = clamp(Math.round(factors.reduce((acc, f) => acc + f.contribution, 0)));

  // Sin historial declarado la probabilidad no puede apoyarse en él.
  const probabilidad = clamp(
    Math.round(
      noHist
        ? score * 0.85 + sCert * 100 * 0.15
        : score * 0.55 + sOrganismo * 100 * 0.2 + sCategoria * 100 * 0.15 + sCert * 100 * 0.1,
    ),
  );

  const riesgo: NivelCualitativo = licitacion.riesgo;
  const competencia = competenciaEstimada(licitacion);
  const complejidadNivel = complejidad;

  const proRazones: string[] = [];
  const contraRazones: string[] = [];

  if (sRubro > 0.6) proRazones.push("Alineado con tu rubro principal");
  if (sOrganismo > 0.3) proRazones.push("Historial positivo con el organismo");
  if (sCategoria > 0.3) proRazones.push(`Tasa de éxito histórica ${Math.round(sCategoria * 100)}% en la categoría`);
  if (sPresupuesto > 0.8) proRazones.push("Monto dentro de tu sweet-spot operativo");
  if (sRegion === 1) proRazones.push("Región preferida por la empresa");
  if (sCert > 0.7) proRazones.push("Cumples certificaciones clave");

  if (sRubro < 0.3) contraRazones.push("Categoría fuera de tu rubro principal");
  if (!noHist && sCategoria < 0.1) contraRazones.push("Sin historial de adjudicación en la categoría");
  if (licitacion.montoEstimado != null && sPresupuesto < 0.4) contraRazones.push("Monto fuera del rango estratégico");
  if (riesgo === "alto" && profile.estrategia.toleranciaRiesgo !== "alta") contraRazones.push("Riesgo alto sobre tu tolerancia configurada");
  if (competencia === "alto") contraRazones.push("Alta competencia esperada");
  if (complejidadNivel === "alto" && profile.estrategia.complejidadAceptada !== "alta") contraRazones.push("Complejidad excede tu capacidad configurada");

  let recommendation: Recommendation;
  if (score >= 75 && probabilidad >= 55 && contraRazones.length <= 1) {
    recommendation = "postular";
  } else if (score < 45 || contraRazones.length >= 3 || probabilidad < 25) {
    recommendation = "no_postular";
  } else {
    recommendation = "revisar";
  }

  return {
    score, probabilidad, recommendation, riesgo, competencia, complejidad: complejidadNivel,
    factors, proRazones, contraRazones,
  };
}

export function levelLabel(n: NivelCualitativo): string {
  if (n === "sin_dato") return "Sin dato";
  return n === "alto" ? "Alta" : n === "medio" ? "Media" : "Baja";
}

export function recommendationLabel(r: Recommendation): string {
  return r === "postular" ? "POSTULAR" : r === "no_postular" ? "NO POSTULAR" : "REVISAR";
}

export { levelFromScore };
